import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronUpDownIcon, XMarkIcon, PhotoIcon, TrashIcon } from '@heroicons/react/24/outline';
import { confirmDialog } from '@/components/ConfirmDialog';
import { promptDialog } from '@/components/PromptDialog';
import { Tip } from '@/components/ui/tip';
import { disconnectWesSocket, getWesSocket } from '@/lib/wesSocket';

type WorkUnit = {
  id: string;
  title: string;
  detail?: string | null;
  status: string;
  orderId?: string;
  slaSeconds?: number | null;
  elapsedSeconds?: number;
  remainingSeconds?: number | null;
  slaStatus?: 'ok' | 'warning' | 'late';
  slaColor?: 'blue' | 'amber' | 'red';
  order?: {
    id?: string | null;
    appointmentCode?: string | null;
    serviceType?: string | null;
    scheduledDate?: string | null;
    clientId?: string | null;
    vehicleId?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    companyName?: string | null;
    make?: string | null;
    model?: string | null;
    licensePlate?: string | null;
  } | null;
};

const formatSeconds = (value?: number | null) => {
  if (value === null || value === undefined) return '--:--';
  const total = Math.max(0, Math.floor(value));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const statusTone = (color?: string) => {
  if (color === 'red') return 'bg-red-500';
  if (color === 'amber') return 'bg-amber-500';
  return 'bg-blue-500';
};


const WesOperator: React.FC = () => {
  const [current, setCurrent] = useState<WorkUnit | null>(null);
  const [next, setNext] = useState<WorkUnit | null>(null);
  const [tick, setTick] = useState(0);
  const lastSyncRef = useRef<number>(Date.now());

  // Historial del vehículo
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [vehicleHistory, setVehicleHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistoryModal = async () => {
    const vehicleId = current?.order?.vehicleId;
    if (!vehicleId) return;
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/api/appointments-enhanced?vehicleId=${vehicleId}`);
      const all: any[] = res.data?.appointments || [];
      // Excluir el turno actual y ordenar del más reciente al más viejo
      const history = all
        .filter((a) => a.id !== current?.order?.id && a.status === 'completed')
        .sort((a, b) => new Date(b.scheduledDate).getTime() - new Date(a.scheduledDate).getTime());
      setVehicleHistory(history);
    } catch (_) {
      setVehicleHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };


  const fetchQueue = useCallback(async () => {
    try {
      const response = await axios.get('/api/wes/operator/next');
      setCurrent(response.data.current || null);
      setNext(response.data.next || null);
      lastSyncRef.current = Date.now();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar tareas');
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const socket = getWesSocket();
    const handleUpdate = () => fetchQueue();
    socket.on('wes:update', handleUpdate);
    // Al reconectar despues de un corte, re-fetchear para no quedar con datos rancios
    socket.on('connect', fetchQueue);
    return () => {
      socket.off('wes:update', handleUpdate);
      socket.off('connect', fetchQueue);
      disconnectWesSocket();
    };
  }, [fetchQueue]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const withElapsed = useCallback((value?: number | null, status?: string) => {
    if (value === null || value === undefined) return value;
    if (status !== 'in_progress') return value;
    const delta = Math.floor((Date.now() - lastSyncRef.current) / 1000);
    return value + Math.max(0, delta);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick]);

  const currentWithElapsed = useMemo(() => {
    if (!current) return null;
    return {
      ...current,
      elapsedSeconds: withElapsed(current.elapsedSeconds, current.status),
    };
  }, [current, withElapsed]);

  const handleStart = async () => {
    if (!current) return;
    try {
      await axios.post(`/api/wes/work-units/${current.id}/start`);
      await fetchQueue();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo iniciar');
    }
  };

  const handleComplete = async () => {
    if (!current) return;
    try {
      await axios.post(`/api/wes/work-units/${current.id}/complete`);
      await fetchQueue();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo finalizar');
    }
  };

  const handleBlock = async () => {
    if (!current) return;
    const reason = await promptDialog({ title: 'Bloquear trabajo', description: 'Motivo del bloqueo.', confirmText: 'Bloquear' });
    if (!reason) return;
    try {
      await axios.post(`/api/wes/work-units/${current.id}/block`, { reason });
      await fetchQueue();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo bloquear');
    }
  };

  const handleUnblock = async () => {
    if (!current) return;
    try {
      await axios.post(`/api/wes/work-units/${current.id}/unblock`, { reason: 'Reanudado por operador' });
      await fetchQueue();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'No se pudo reanudar');
    }
  };


  const currentClient = useMemo(() => {
    if (!current?.order) return '';
    return current.order.companyName
      ? current.order.companyName
      : `${current.order.firstName || ''} ${current.order.lastName || ''}`.trim();
  }, [current]);

  const currentVehicleInfo = useMemo(() => {
    if (!current?.order) return '';
    const make = current.order.make || '';
    const model = current.order.model || '';
    const license = current.order.licensePlate || '';
    const nameParts = [make, model].filter(Boolean).join(' ');
    if (nameParts && license) return `${nameParts} - ${license}`;
    return nameParts || license;
  }, [current]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">WES Operador</h1>
            <p className="text-sm text-muted-foreground">Tu proxima accion</p>
          </div>
        </div>

        <Card className="border-2 border-muted">
          <CardContent className="p-6 space-y-6">
            {currentWithElapsed ? (
              <>
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Orden</p>
                    <p className="text-xl font-semibold">
                      {currentWithElapsed.order?.appointmentCode || currentWithElapsed.order?.serviceType || 'OT'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {currentClient || 'Cliente sin asignar'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {currentVehicleInfo || '-'}
                    </p>
                    {current?.order?.vehicleId && (
                      <button
                        type="button"
                        onClick={openHistoryModal}
                        className="mt-1 text-xs text-cyan-500 underline underline-offset-2 hover:text-cyan-400"
                      >
                        Ver historial del vehículo
                      </button>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Tiempo</p>
                    <p className="text-3xl font-semibold">
                      {formatSeconds(currentWithElapsed.elapsedSeconds)} / {formatSeconds(currentWithElapsed.slaSeconds)}
                    </p>
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <span className={`h-2 w-2 rounded-full ${statusTone(currentWithElapsed.slaColor)}`} />
                      <span className="text-xs text-muted-foreground">
                        {currentWithElapsed.slaStatus === 'late' ? 'Atrasado' : currentWithElapsed.slaStatus === 'warning' ? 'En riesgo' : 'En tiempo'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-muted p-4">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Trabajo actual</p>
                  <p className="text-3xl font-bold">{currentWithElapsed.title}</p>
                  {currentWithElapsed.detail && <p className="text-sm text-muted-foreground mt-2">{currentWithElapsed.detail}</p>}
                </div>

                <div className="flex flex-wrap gap-3">
                  {currentWithElapsed.status === 'assigned' && (
                    <Button className="text-base px-6 py-5" onClick={handleStart}>
                      Iniciar
                    </Button>
                  )}
                  {currentWithElapsed.status === 'in_progress' && (
                    <>
                      <Button className="text-base px-6 py-5" onClick={handleComplete}>
                        Finalizar
                      </Button>
                      <Button variant="outline" className="text-base px-6 py-5" onClick={handleBlock}>
                        Bloquear
                      </Button>
                    </>
                  )}
                  {currentWithElapsed.status === 'blocked' && (
                    <Button className="text-base px-6 py-5" onClick={handleUnblock}>
                      Reanudar
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg font-semibold">Sin tareas asignadas</p>
                <p className="text-sm text-muted-foreground mt-2">El sistema asignara la proxima accion automaticamente.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Siguiente</p>
            <p className="text-lg font-semibold">
              {next ? next.title : 'Sin tareas en cola'}
            </p>
          </CardContent>
        </Card>
      </div>



      {/* Modal: Historial del vehículo */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial del vehículo — {current?.order?.licensePlate || '-'}</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          ) : vehicleHistory.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Sin servicios anteriores registrados para este vehículo.</p>
          ) : (
            <div className="space-y-2">
              {vehicleHistory.map((appt) => (
                <div key={appt.id} className="rounded-lg border p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{appt.appointmentCode || '-'}</span>
                    <span className="text-xs text-muted-foreground">
                      {appt.scheduledDate ? new Date(appt.scheduledDate).toLocaleDateString('es-AR') : '-'}
                    </span>
                  </div>
                  <p className="text-sm font-medium">{appt.serviceType || 'Servicio'}</p>
                  {appt.notes && <p className="text-xs text-muted-foreground line-clamp-2">{appt.notes}</p>}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setShowHistoryModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default WesOperator;
